import React from "react";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import tempImage from "../../assets/images/temp.jpg"; // Placeholder image

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: 20px;
`;

const Section = styled.div`
  margin-top: 40px;
`;

const Paragraph = styled.p`
  font-size: 1.1em;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const SubTitle = styled.h2`
  margin-top: 40px;
  font-size: var(--font-size-subtitle);
`;

const Button = styled.a`
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 10px 20px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  margin: 20px 0;
  cursor: pointer;
  border-radius: 4px;

  &:hover {
    background-color: var(--accent-color);
  }
`;

const List = styled.ul`
  columns: 2;
  -webkit-columns: 2;
  -moz-columns: 2;
  list-style-type: none;
  padding: 0;

  @media (max-width: 768px) {
    columns: 1;
    -webkit-columns: 1;
    -moz-columns: 1;
  }
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 25px;
  margin-bottom: 10px;

  &::before {
    content: "✔";
    position: absolute;
    left: 0;
    color: var(--primary-color);
  }
`;

const RedListItem = styled(ListItem)`
  &::before {
    content: "✘";
    color: red;
  }
`;

const CardContainer = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 40px;
`;

const Card = styled.div`
  background-color: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  width: 300px;
  text-align: center;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const CardTitle = styled.h3`
  font-size: 1.5em;
  margin-bottom: 10px;
`;

const CardDescription = styled.p`
  font-size: 1em;
  margin-bottom: 20px;
`;

const DonateItems = () => {
  return (
    <>
      <Helmet>
        <title>Donate Items - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Donate items to Habitat for Humanity Buffalo to help us build and renovate homes for families in need. Learn what items we accept and how you can contribute."
        />
      </Helmet>
      <Container>
        <Title>Donate Items</Title>
        <SubTitle className="section-header primary">
          Donated Items Help Us Build Houses!
        </SubTitle>
        <Paragraph>
          Gifts-in-kind donations of bulk materials and services are a vital
          part of Habitat for Humanity Buffalo's mission and help our affiliate
          save thousands of dollars every year. When organizations donate goods
          or services, those donations are considered gifts-in-kind. Our
          corporate partnerships help make our work at Habitat Buffalo possible.
          Your company can partner with Habitat Buffalo by making an in-kind
          donation to help us build and renovate homes, through our events, or
          to support our overall operations.
        </Paragraph>
        <Paragraph>
          Gifts-in-kind donations of all sorts have helped us further our work
          and bring opportunity and resources to families in need of a decent
          place to call home. Contributions large and small continue to make a
          difference, and we believe there is something every company can give
          to allow us to help even more families build better futures.
        </Paragraph>

        <SubTitle className="section-header green">
          ReStore Items We Accept
        </SubTitle>
        <Paragraph>We accept a wide range of items, including:</Paragraph>
        <List>
          <ListItem>Furniture (in good condition)</ListItem>
          <ListItem>Appliances (10 years or newer)</ListItem>
          <ListItem>Cabinets and Countertops</ListItem>
          <ListItem>Doors (solid & hollow-core)</ListItem>
          <ListItem>Windows (double-pane vinyl, undamaged)</ListItem>
          <ListItem>Lighting and Electrical fixtures</ListItem>
          <ListItem>Plumbing fixtures and fittings</ListItem>
          <ListItem>Building materials</ListItem>
          <ListItem>Tools and Equipment</ListItem>
          <ListItem>Lawn, Garden, and Seasonal items</ListItem>
          <ListItem>Antiques, Art, and Home Décor</ListItem>
        </List>

        <Section>
          <SubTitle className="section-header primary">
            Construction Materials Needed
          </SubTitle>
          <Paragraph>
            Habitat Buffalo accepts large quantities of in-kind donations such
            as building materials, appliances, and more. A few examples of items
            we need include:
          </Paragraph>
          <List>
            <ListItem>Siding </ListItem>
            <ListItem>Flooring</ListItem>
            <ListItem>Roofing Materials</ListItem>
            <ListItem>Lumber</ListItem>
            <ListItem>Paint</ListItem>
            <ListItem>Hardware</ListItem>
            <ListItem>Insulation</ListItem>
            <ListItem>Fixtures</ListItem>
          </List>
        </Section>

        <SubTitle className="section-header orange">
          Items We Do Not Accept
        </SubTitle>
        <List>
          <RedListItem>Used mattresses</RedListItem>
          <RedListItem>Hazardous materials</RedListItem>
          <RedListItem>Items in poor condition</RedListItem>
          <RedListItem>Frameless mirrors or glass</RedListItem>
          <RedListItem>Entertainment centers</RedListItem>
          <RedListItem>
            Large desks, medical equipment, or water beds
          </RedListItem>
          <RedListItem>Appliances over 10 years old</RedListItem>
          <RedListItem>Dehumidifiers or water softeners</RedListItem>
          <RedListItem>Dishwashers</RedListItem>
          <RedListItem>Cast iron or undermount sinks</RedListItem>
          <RedListItem>Used hot water tanks</RedListItem>
          <RedListItem>Shower doors or surrounds</RedListItem>
          <RedListItem>Dirty or old toilets</RedListItem>
          <RedListItem>Single drawers or cabinet doors</RedListItem>
          <RedListItem>Single-pane or storm windows</RedListItem>
          <RedListItem>Wood windows</RedListItem>
          <RedListItem>Drapery, blinds, or other window coverings</RedListItem>
          <RedListItem>Ceiling fans</RedListItem>
          <RedListItem>Used light bulbs, CFL bulbs</RedListItem>
          <RedListItem>Used paint, stain, or solvent</RedListItem>
          <RedListItem>Scrap pieces of flooring</RedListItem>
          <RedListItem>Previously installed tile or grout</RedListItem>
          <RedListItem>
            Linens, bedding, towels, draperies, books, magazines, clothing,
            children's toys or furniture
          </RedListItem>
          <RedListItem>Musical instruments, organs, or pianos</RedListItem>
          <RedListItem>
            Battery powered tools without batteries and charger
          </RedListItem>
          <RedListItem>Flammable or combustible chemicals</RedListItem>
          <RedListItem>Automotive parts, fluids, or containers</RedListItem>
        </List>

        <Section>
          <SubTitle className="section-header green">Donation Options</SubTitle>
          <CardContainer>
            <Card>
              <CardTitle>For Individuals</CardTitle>
              <CardDescription>
                If you are looking to make a one-time, personal donation to our
                ReStores.
              </CardDescription>
              <Button
                href="https://onlinedonationpickup.com/habitatbuffalo/#intro
"
              >
                Donate Items
              </Button>
            </Card>
            <Card>
              <CardTitle>For Organizations</CardTitle>
              <CardDescription>
                Organizations can donate large quantities of items to our
                ReStores.
              </CardDescription>
              <Button href="/form/donation">Donate as an Organization</Button>
            </Card>
          </CardContainer>
        </Section>
      </Container>
    </>
  );
};

export default DonateItems;
